import { CachePolicies, useFetch } from 'use-http';
import { routes } from '@/webapi/routes';

export function useStoreSettingsApi() {
  const { get, post, loading } = useFetch(``, (globalOptions) => ({
    ...globalOptions,
    ...{
      credentials: `include`,
      cachePolicy: CachePolicies.NO_CACHE,
      loading: false,
    },
  }));

  const listReviewsProviders = async (): Promise<ReviewProviders> =>
    (await get(routes.reviewsEndpoint())) as ReviewProviders;

  const setReviewsProvider = async (
    providerName: ReviewProviderName,
  ): Promise<void> => post(routes.reviewsEndpoint(), { value: providerName });

  const getProductExcludedTags = async (): Promise<ProductExcludedTags> =>
    (await get(routes.getProductExcludedTags())) as ProductExcludedTags;

  const setProductExcludedTags = async (
    tags: string[],
  ): Promise<ProductExcludedTags> =>
    post(routes.upsertProductExcludedTags(), { tags });

  const setBlockedIps = async (ips: string[]): Promise<IpBlockList> => {
    const cleaned = ips
      .map((x) => (x ?? ``).trim())
      .filter((x) => x.length > 0);

    await post(routes.upsertIpBlockList(), {
      ipBlockList: cleaned.map((cidr) => ({ cidr })),
    });

    return getBlockedIpsByStore();
  };

  const getBlockedIpsByStore = async (): Promise<IpBlockList> => {
    const res = (await get(routes.getIpBlocksByStore())) as
      | {
          ipBlockList?: Array<{ cidr?: string | null }>;
        }
      | undefined;

    const ips = Array.isArray(res?.ipBlockList)
      ? res.ipBlockList
          .map((b) => (b?.cidr ?? ``).trim())
          .filter((v): v is string => v.length > 0)
      : [];

    return { ips: [...new Set(ips)] };
  };

  const getStoreGoogleTags = async (): Promise<StoreGoogleTags> =>
    (await get(routes.getStoreGoogleTags())) as StoreGoogleTags;

  const setStoreGoogleTags = async (tags: string[]): Promise<StoreGoogleTags> =>
    post(routes.upsertStoreGoogleTags(), { tags });

  const setIsShopRTL = async (isRtlValue: boolean): Promise<void> =>
    post(routes.setShopRTL(), { isRtlValue });

  const isShopRTL = async (): Promise<IsRtlValue> =>
    (await get(routes.isShopRTL)) as IsRtlValue;

  const getVariantMetafields = async (): Promise<VariantMetafieldsResp> =>
    (await get(routes.getStoreVariantMetafields())) as VariantMetafieldsResp;

  const getUserSettings = (): Promise<UserSettings> =>
    get(routes.userSettings) as Promise<UserSettings>;

  const getPostPurchaseSettings = (): Promise<{ value: string }> =>
    get(routes.postPurchaseSettings) as Promise<{ value: string }>;

  const setPostPurchaseSettings = (
    v: 'true' | 'false',
  ): Promise<{ value: string }> =>
    post(routes.postPurchaseSettings, { value: v }) as Promise<{
      value: string;
    }>;

  const setUserSettings = (settings: {
    isConfidenceIntervalsEnabled: 'YES' | 'NO';
  }): Promise<UserSettings> =>
    post(routes.userSettings, settings) as Promise<UserSettings>;

  const setVariantMetafields = async (
    metafields: Metafield[],
  ): Promise<VariantMetafieldsResp> =>
    post(routes.setStoreVariantMetafields(), { metafields });

  const getDefaultSessionView = async (): Promise<string> => {
    const result = await get(routes.sessionView);
    return result.view as string;
  };

  const setDefaultSessionView = async (view: string): Promise<void> =>
    post(routes.sessionView, { view });

  const getProductMetafields = async (): Promise<ProductMetafieldsResp> =>
    (await get(routes.getStoreProductMetafields())) as ProductMetafieldsResp;

  const getGeneralStoreSettings = async (): Promise<GeneralStoreSettings> =>
    (await get(routes.getStoreGeneralSettings())) as GeneralStoreSettings;

  const setGeneralStoreSettings = async (
    settings: Partial<GeneralStoreSettingsInner>,
  ): Promise<GeneralStoreSettings> =>
    post(routes.setStoreGeneralSettings(), { inner: settings });

  const setProductMetafields = async (
    metafields: Metafield[],
  ): Promise<ProductMetafieldsResp> =>
    post(routes.setStoreProductMetafields(), { metafields });

  return {
    setIsShopRTL,
    isShopRTL,
    listReviewsProviders,
    setReviewsProvider,
    loading,
    getProductExcludedTags,
    setProductExcludedTags,
    getVariantMetafields,
    setVariantMetafields,
    getUserSettings,
    setUserSettings,
    getDefaultSessionView,
    setDefaultSessionView,
    getStoreGoogleTags,
    setStoreGoogleTags,
    setBlockedIps,
    getBlockedIpsByStore,
    getProductMetafields,
    setProductMetafields,
    getPostPurchaseSettings,
    setPostPurchaseSettings,
    getGeneralStoreSettings,
    setGeneralStoreSettings,
  };
}

export interface IpBlockList {
  ips?: string[];
}

export interface ReviewsData {
  reviewsProviders: ReviewProviders;
  reviewsEnabled: boolean;
  hasReviewProviders: boolean;
}

export interface ReviewProviders {
  providers?: ReviewProvider[];
}

export interface ReviewProvider {
  name?: ReviewProviderName;
  canBeSelected?: boolean;
  isSelected?: boolean;
}

export interface ProductExcludedTags {
  tags: string[];
}

export interface Metafield {
  namespace: string;
  keys: string[];
}

export interface MetafieldsResp {
  metafields: Metafield[];
}

export interface IsRtlValue {
  isRtlValue: boolean;
}

export interface StoreGoogleTags {
  tags: string[];
}

export interface ActiveMarket {
  key: string;
  name: string;
}

export interface RecsExcludeVariants {
  checkout?: string[];
  postPurchase?: string[];
  recs?: string[];
}

export interface GeneralStoreSettingsInner {
  activeMarkets?: ActiveMarket[];
  recsExcludeVariants?: RecsExcludeVariants;
  defaultExcludeOutliers?: boolean;
}

export interface GeneralStoreSettings {
  inner: GeneralStoreSettingsInner;
}

export type VariantMetafieldsResp = MetafieldsResp;
export type ProductMetafieldsResp = MetafieldsResp;

export enum ReviewProviderName {
  NA = `____NA`,
  LOOX = `LOOX`,
  OKENDO = `OKENDO`,
  SHOPIFY = `SHOPIFY`,
  KLAVIYO = `KLAVIYO`,
  STAMPED = `STAMPED`,
  YOTPO = `YOTPO`,
}

export type UserSettings = {
  isConfidenceIntervalsEnabled: 'YES' | 'NO';
};

export enum SessionType {
  SESSIONS = `VIEW_SESSIONS`,
  SESSION_WITH_IMPRESSIONS = `VIEW_SWI`,
  USERS = `VIEW_USERS`,
}
